import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { Share2Icon } from "lucide-react";
import { useId, useState } from "react";

import { HeroAvatar, useHeroLookup } from "@/features/matches";
import { useApiErrorMessage } from "@/shared/api";
import { BlockError } from "@/shared/ui/block-error";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useHighlightsAllowed } from "../../api/useHighlightsAllowed";
import { useHighlightsOverview } from "../../api/useHighlightsOverview";
import { useRequestClips } from "../../api/useRequestClips";
import { byBestThenTime, canRequestClip, isClipWorking } from "../../model/clipState";
import type { HighlightView } from "../../model/Highlight";
import { overviewItems } from "../../model/overviewItems";
import { ClipDialog } from "../ClipDialog";
import {
  ClipAction,
  ClipThumb,
  HighlightSummary,
  useHighlightMeta,
  useHighlightTitle,
} from "../HighlightParts";

const HEADING = msg`Your best moments`;
const LEAD = msg`Relock finds them in your matches and makes clips`;
const ALL = msg`All highlights · {count}`;
const WATCH = msg`Watch: {title}`;
const SHARE = msg`Share`;
const LOAD_ERROR = msg`Could not load your highlights`;
// Карточек в ряд: главный клип занимает две колонки, рядом помещаются две карточки;
// без главного — четыре.
const CARDS_WITH_MAIN = 2;
const CARDS_ALONE = 4;

// «Твои лучшие моменты» на главной по макету design/prototype/HighlightsHome.dc.html:
// последний готовый клип крупно с «Поделиться», рядом ещё клипы и моменты, которые можно
// сделать. Хайлайтов нет или они закрыты для игрока — блока нет.
export const HighlightsStrip = () => {
  const { i18n } = useLingui();
  const headingId = useId();
  const overview = useHighlightsOverview();
  const requestClips = useRequestClips();
  const requestError = useApiErrorMessage(requestClips.error);
  const title = useHighlightTitle();
  const meta = useHighlightMeta();
  const heroLookup = useHeroLookup();
  const [openId, setOpenId] = useState<number | null>(null);
  const allowed = useHighlightsAllowed();

  if (!allowed) {
    return null;
  }
  if (overview.isPending) {
    return <Skeleton className="h-80 rounded-[14px]" />;
  }
  if (overview.isError) {
    return (
      <BlockError
        title={i18n._(LOAD_ERROR)}
        retrying={overview.isFetching}
        onRetry={() => void overview.refetch()}
      />
    );
  }
  const { data } = overview;
  const all = overviewItems(data);
  const waiting = data.matches
    .flatMap((match) => match.items)
    .filter((highlight) => canRequestClip(highlight) || isClipWorking(highlight))
    .sort(byBestThenTime);
  const [main, ...moreReady] = data.ready;
  const cards = [...moreReady, ...waiting].slice(
    0,
    main === undefined ? CARDS_ALONE : CARDS_WITH_MAIN,
  );
  if (main === undefined && cards.length === 0) {
    return null;
  }
  const watch = (highlight: HighlightView) =>
    i18n._({ ...WATCH, values: { title: title(highlight) } });
  const open = all.find((highlight) => highlight.id === openId) ?? null;
  const mainHero = main === undefined ? null : heroLookup(main.hero_id);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
        <span className="flex flex-col gap-0.5">
          <h2 id={headingId} className="font-display text-[28px] leading-tight">
            {i18n._(HEADING)}
          </h2>
          <span className="text-sm text-muted">{i18n._(LEAD)}</span>
        </span>
        <Link
          to="/highlights"
          className="text-sm font-semibold text-accent no-underline hover:text-accent-strong"
        >
          {i18n._({ ...ALL, values: { count: all.length } })}
          <span aria-hidden="true"> →</span>
        </Link>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] items-start gap-3.5">
        {main !== undefined && mainHero !== null && (
          <article className="flex flex-col gap-2.5 rounded-[14px] border border-border-strong bg-surface p-2.5 sm:col-span-2">
            <ClipThumb
              highlight={main}
              size="large"
              label={watch(main)}
              onOpen={() => {
                setOpenId(main.id);
              }}
            />
            <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 px-1 pb-0.5">
              <HeroAvatar name={mainHero.name} iconUrl={mainHero.iconUrl} size={40} />
              <span className="flex min-w-0 flex-[999_1_220px] flex-col leading-snug">
                <span className="text-lg font-semibold">{title(main)}</span>
                <HighlightSummary highlight={main} className="text-sm text-muted" />
              </span>
              <Button
                className="h-11 px-4"
                onClick={() => {
                  setOpenId(main.id);
                }}
              >
                <Share2Icon aria-hidden="true" />
                {i18n._(SHARE)}
              </Button>
            </div>
          </article>
        )}
        {cards.map((highlight) => (
          <article
            key={highlight.id}
            className="flex flex-col gap-2 rounded-[14px] border border-border bg-surface p-2.5"
          >
            <ClipThumb
              highlight={highlight}
              label={watch(highlight)}
              onOpen={() => {
                setOpenId(highlight.id);
              }}
            />
            <span className="flex flex-col px-1 leading-snug">
              <span className="font-semibold">{title(highlight)}</span>
              <span className="text-[13px] text-subtle">{meta(highlight)}</span>
            </span>
            <ClipAction
              highlight={highlight}
              clipsEnabled={data.clips_enabled}
              requesting={requestClips.pendingIds.includes(highlight.id)}
              onRequest={() => {
                requestClips.request([highlight.id]);
              }}
              className="mt-auto px-1"
            />
          </article>
        ))}
      </div>
      {requestError !== null && (
        <p role="alert" className="text-sm text-bad">
          {requestError}
        </p>
      )}

      <ClipDialog
        highlight={open}
        match={data.matches.find((match) => match.match_id === open?.match_id)}
        onOpen={(highlight) => {
          setOpenId(highlight.id);
        }}
        onClose={() => {
          setOpenId(null);
        }}
      />
    </section>
  );
};
